import { getApiClient } from '@apps/web/lib/api_client';
import {
    notificationFollowsRequest,
    notificationPreferencesRequest,
} from '@apps/web/lib/member_settings_api';
import { type TraceParams } from '@apps/web/swr_data_fetching/query_trace_params';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import { type ApiBodyType, type Mode } from '@libraries/typescript/api_client';

export function useUser(traceParams: TraceParams) {
    return useApiQuery({
        path: '/users/me',
        method: 'get',
        select: (response) => response.user,
        reason: traceParams.reason,
    });
}

// The signed identity lasts 12 hours; refreshing hourly keeps a page left
// open signed when it next updates the Messenger.
const INTERCOM_MESSENGER_TOKEN_REFRESH_MS = 60 * 60 * 1000;

/**
 * The signed Intercom identity for the current user, or null until the
 * Messenger secret is configured. The organization is signed in only when
 * the user is a member of it.
 */
export function useIntercomMessengerToken(
    userId: string | null,
    organizationId: string | null,
    options: { readonly enabled: boolean } & TraceParams,
) {
    return useApiQuery({
        path: '/users/me/intercom-messenger-token',
        method: 'get',
        queryParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        // The token signs one user. Keep each user's token apart so a person
        // who signs in on the same page never gets the previous user's.
        cacheIdentity: userId ?? undefined,
        enabled: options.enabled && userId !== null,
        select: (response) => response.token,
        reason: options.reason,
        refreshInterval: INTERCOM_MESSENGER_TOKEN_REFRESH_MS,
    });
}

export function useEmailNotificationPreferences(
    organizationId: string | null,
    reason?: string,
) {
    return useApiQuery({
        path: '/users/me/email-notification-preferences',
        method: 'get',
        queryParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason,
    });
}

export function useNotificationPreferences(
    organizationId: string | null,
    reason?: string,
    mode?: Mode,
) {
    return useApiQuery({
        ...notificationPreferencesRequest(organizationId),
        method: 'get',
        enabled: !!organizationId,
        select: (response) => response,
        reason,
        mode,
    });
}

// This gets user and member profile transactionally.
export function useUserProfile(traceParams: TraceParams) {
    return useApiQuery({
        path: '/users/me/profile',
        method: 'get',
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrgMemberProfiles(traceParams: TraceParams) {
    return useApiQuery({
        path: '/users/me/org-member-profiles',
        method: 'get',
        select: (response) => response.profiles,
        reason: traceParams.reason,
    });
}

// Update the authenticated user's own membership for one organization
// (e.g. their assigned office). Only set fields are persisted.
export async function updateMeOrgMemberProfile(
    organizationId: string,
    body: ApiBodyType<
        '/users/me/org-member-profiles/{organization_id}',
        'patch'
    >,
) {
    return getApiClient().patch({
        url: '/users/me/org-member-profiles/{organization_id}',
        pathParams: { organization_id: organizationId },
        body,
    });
}

export async function updateMeTestingRole(
    body: ApiBodyType<'/users/me/testing-role', 'post'>,
) {
    return getApiClient().post({
        url: '/users/me/testing-role',
        body,
    });
}

export function useTestingRoleSwitchAccess(traceParams: TraceParams) {
    return useApiQuery({
        path: '/users/me/testing-role-access',
        method: 'get',
        select: (response) => response.can_switch_testing_role,
        reason: traceParams.reason,
        dedupingInterval: 0,
    });
}

// PHO-9323 notification follows & outreach subscribers hooks.

export function useNotificationFollows(
    organizationId: string | null,
    reason?: string,
    memberUserId?: string,
) {
    return useApiQuery({
        ...notificationFollowsRequest(organizationId, memberUserId),
        method: 'get',
        enabled: !!organizationId,
        select: (response) => response,
        reason,
    });
}
